<script lang="ts" setup>
  import { useGetPublicMetersByIdentifiers } from "@/api/meter-point/meter-point"
  import { useCotMoveIn } from "@/lib/useCotMoveIn.ts"
  import { useStepWizard } from "@/lib/useStepWizard"
  import { MeterpointReadings } from "@/types/MeterpointReading.ts"
  import dayjs from "dayjs"
  import customParseFormat from "dayjs/plugin/customParseFormat"
  import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
  import { computed, ref, useTemplateRef, watch } from "vue"
  import Alert from "@/components/ui/alert/Alert.vue"
  import { Button } from "@/components/ui/button"
  import Checkbox from "@/components/ui/inputs/checkbox/Checkbox.vue"
  import { StepWizardProgressCard } from "@/components/ui/step-wizard"

  import MeterRead from "@/views/cot-move-in/components/MeterRead.vue"

  import ReadingMeterDialog from "./components/ReadingMeterDialog.vue"

  dayjs.extend(customParseFormat)
  dayjs.extend(isSameOrAfter)

  const { goNext } = useStepWizard()
  const { updateMeterReading, cotMoveInExtras } = useCotMoveIn()

  const meterReadRef = useTemplateRef("meterReadRef")
  const isSameDateForAllReadings = ref(false)

  const meterReadings = ref<MeterpointReadings[]>([])

  const { data: meters, mutateAsync } = useGetPublicMetersByIdentifiers()

  const meterpointIdentifiers = computed(
    () => cotMoveInExtras.value.meterpointIdentifiers,
  )

  watch(
    meterpointIdentifiers,
    async (val) => {
      await mutateAsync({ data: { identifiers: val } })
    },
    { immediate: true },
  )

  const isAllReadingsOverThreshold = ref(true)

  const submit = async () => {
    if (meterReadRef.value) {
      await Promise.all(
        meterReadRef.value.map((meterRef) => meterRef?.validate()),
      )

      isAllReadingsOverThreshold.value = meterReadRef.value.every(
        (meterRef) => meterRef?.isAllReadingsOverThreshold,
      )

      if (meterReadRef.value.some((meterRef) => !meterRef?.formMeta.valid))
        return
    }

    updateMeterReading(
      meterReadings.value.filter((meterReading) => meterReading.isValid),
    )
    goNext()
  }

  const skipStep = async () => {
    updateMeterReading([])
    goNext()
  }

  const updateAllDates = (readingDate?: string) => {
    if (!isSameDateForAllReadings.value || !readingDate) return
    meterReadings.value =
      meters.value?.map((_, index) => ({
        ...meterReadings.value[index],
        readingDate,
      })) ?? []
  }
</script>

<template>
  <StepWizardProgressCard allow-skip title="Submit meter reading">
    <div class="space-y-8">
      <p>
        Even if you have a smart meter, we still ask for an opening reading to
        make sure everything starts off correctly, especially if the meter
        hasn’t communicated with us yet. Please check your meter and enter the
        numbers exactly as they appear (ignoring any red digits or numbers after
        a decimal point).
      </p>

      <Checkbox
        v-model:checked="isSameDateForAllReadings"
        label="Use same date for all readings"
        name="isSameDateForAllReadings"
      />

      <div v-if="meters?.length" class="space-y-8 sm:space-y-2">
        <div class="space-y-10">
          <MeterRead
            v-for="(meter, index) in meters"
            :key="meter.meterpointID"
            ref="meterReadRef"
            v-model="meterReadings[index]"
            :meter="meter"
            @update-date="updateAllDates"
          />

          <Alert v-if="!isAllReadingsOverThreshold" variant="error">
            Your reading appears lower than expected. If you're confident it's
            correct, you can skip this step. Once you're logged into your
            account, simply follow the steps to submit your opening meter
            reading.
          </Alert>
        </div>
        <ReadingMeterDialog />
      </div>
    </div>
    <template #skip_button>
      <Button size="md" variant="text" @click="skipStep">Skip For Now</Button>
    </template>
    <template #next_button>
      <Button size="md" variant="tonal" @click="submit">Next</Button>
    </template>
  </StepWizardProgressCard>
</template>
